import { proxy } from 'valtio';
import type { NivaasPackage } from '../types/package';
import { PackageType, isDefaultPackage, isCustomPackage } from '../types/package';

/**
 * Global packages state - populated from root loader
 */
export const packagesState = proxy<{
  packages: NivaasPackage[];
  initialized: boolean;
}>({
  packages: [],
  initialized: false,
});

/**
 * Initialize packages state from loader data
 */
export function initializePackages(packages: NivaasPackage[]) {
  packagesState.packages = packages;
  packagesState.initialized = true;
}

/**
 * Get package by ID
 */
export function getPackageById(id: string): NivaasPackage | undefined {
  return packagesState.packages.find((p) => p.id === id);
}

/**
 * Get package by slug
 */
export function getPackageBySlug(slug: string): NivaasPackage | undefined {
  return packagesState.packages.find((p) => p.slug === slug);
}

/**
 * Get all packages that include a specific offering (by ID)
 */
export function getPackagesForOffering(offeringId: string): NivaasPackage[] {
  return packagesState.packages.filter((p) =>
    p.allowedOfferings.some((o) => o.id === offeringId),
  );
}

/**
 * Individual package item with calculated costs
 */
export interface PackageItemCost {
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
  gst: number;
  gstPercentage: number;
}

/**
 * Full package cost breakdown including individual items
 */
export interface PackageCostBreakdown {
  /** Total cost before GST */
  amount: number;
  /** Currency code */
  currency: string;
  /** Total GST amount */
  gst: number;
  /** Highest GST percentage (for display) */
  gstPercentage: number;
  /** Individual item breakdown */
  items: PackageItemCost[];
}

/**
 * Calculate the total supplementary items cost for a package (simple version)
 * @deprecated Use getPackageCostBreakdown for full details
 */
export function getPackageSupplementaryCost(
  pkg: NivaasPackage | null | undefined,
): { amount: number; currency: string; gst: number } | null {
  const breakdown = getPackageCostBreakdown(pkg);
  if (!breakdown) return null;
  return {
    amount: breakdown.amount,
    currency: breakdown.currency,
    gst: breakdown.gst,
  };
}

/**
 * Calculate the full cost breakdown for a package's supplementary items
 * Returns individual item costs, totals, and GST details
 */
export function getPackageCostBreakdown(
  pkg: NivaasPackage | null | undefined,
): PackageCostBreakdown | null {
  if (!pkg?.supplementaryItems || pkg.supplementaryItems.length === 0) {
    return null;
  }

  let totalAmount = 0;
  let totalGst = 0;
  let maxGstPercentage = 0;
  let currency = 'INR';
  const items: PackageItemCost[] = [];

  for (const item of pkg.supplementaryItems) {
    const itemTotal = item.price.amount * item.quantity;
    const itemGst = itemTotal * (item.gst / 100);

    items.push({
      name: item.name,
      quantity: item.quantity,
      unitPrice: item.price.amount,
      total: itemTotal,
      gst: itemGst,
      gstPercentage: item.gst,
    });

    totalAmount += itemTotal;
    totalGst += itemGst;
    currency = item.price.currency;

    if (item.gst > maxGstPercentage) {
      maxGstPercentage = item.gst;
    }
  }

  return {
    amount: totalAmount,
    currency,
    gst: totalGst,
    gstPercentage: maxGstPercentage,
    items,
  };
}

/**
 * Check if a package is currently available (based on dates)
 */
export function isPackageAvailable(pkg: NivaasPackage): boolean {
  const now = new Date();
  const fromDate = new Date(pkg.availableFromDate);
  const untilDate = new Date(pkg.availableUntilDate);

  return now >= fromDate && now <= untilDate;
}

/**
 * Get all available packages (filtered by current date)
 */
export function getAvailablePackages(): NivaasPackage[] {
  return packagesState.packages.filter(isPackageAvailable);
}

/**
 * Get the workspace's default (base pricing) package.
 * Returns undefined if no default package exists.
 * 
 * Note: There should be exactly one DEFAULT package per workspace.
 */
export function getDefaultPackage(): NivaasPackage | undefined {
  return packagesState.packages.find(isDefaultPackage);
}

/**
 * Get all custom packages (non-default packages).
 */
export function getCustomPackages(): NivaasPackage[] {
  return packagesState.packages.filter(isCustomPackage);
}

/**
 * Get the default package (there should only be one per workspace).
 * @deprecated Use getDefaultPackage() instead.
 */
export function getDefaultPackages(): NivaasPackage[] {
  return packagesState.packages.filter(isDefaultPackage);
}

/**
 * Get available custom packages for a specific offering.
 * Returns packages that include this offering in their allowedOfferings.
 */
export function getAvailableCustomPackagesForOffering(offeringId: string): NivaasPackage[] {
  return getCustomPackages().filter(
    (p) =>
      isPackageAvailable(p) && p.allowedOfferings.some((o) => o.id === offeringId),
  );
}

/**
 * Sort packages with DEFAULT first, then CUSTOM.
 * Within each group, sort by name alphabetically.
 */
export function sortPackagesByType(packages: NivaasPackage[]): NivaasPackage[] {
  return [...packages].sort((a, b) => {
    // DEFAULT packages come first
    if (a.packageType !== b.packageType) {
      return a.packageType === PackageType.DEFAULT ? -1 : 1;
    }
    // Within same type, sort by name
    return a.name.localeCompare(b.name);
  });
}

// Re-export the type helpers
export { isDefaultPackage, isCustomPackage, PackageType };
